import React, {useState, useEffect, useRef} from 'react';
import {getBackendOptions, MultiBackend, Tree} from '@minoru/react-dnd-treeview';
import {DndProvider} from 'react-dnd';
import {CssBaseline, ThemeProvider, Button} from '@mui/material';
import Grid from '@mui/material/Grid';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import SourceIcon from '@mui/icons-material/Source';
import FolderDeleteIcon from '@mui/icons-material/FolderDelete';
import {createTheme} from '@mui/material/styles';
import {useNavigate} from "react-router-dom";

import {HierarchyNode} from './HierarchyNode';
import {HierarchyNodeDragPreview} from './HierarchyNodeDragPreview';
import CustomAlert from "../CustomAlert";
import {NODE_LEVEL} from "../../domain/HierarchyConstants";
import {customBackOfficeTheme} from "../themes/customBackOfficeTheme";

export const CONTENT_ACTION_CREATE = 'Create';
export const CONTENT_ACTION_RENAME = 'Rename';
export const CONTENT_ACTION_DELETE = 'Delete';

export default function ContentHierarchyTree({contentData, setSelectedContentDetails, selectedNode, setSelectedNode, setEnableNewContentAction, setEnableAllContentActions}) {
    const navigate = useNavigate();

    const [treeData, setTreeData] = useState([]);
    const [nodeList, setNodeList] = useState([]);
    const [selectedNodeIds, setSelectedNodeIds] = useState([]);
    const [collapsedNodes, setCollapsedNodes] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [enableCreateFolder, setEnableCreateFolder] = useState(false);
    const [enableRenameFolder, setEnableRenameFolder] = useState(false);
    const [enableDeleteFolder, setEnableDeleteFolder] = useState(false);
    const treeRef = useRef(null);

    const treeRootStyle = {height: '100%'};
    const draggingSourceStyle = {opacity: '.3'};
    const dropTargetStyle = {backgroundColor: '#e8f0fe'};

    useEffect(() => {
        setTreeData(contentData);
    }, [contentData]);

    useEffect(() => {
        if (selectedNodeIds.length === 1) {
            setSelectedContentDetails(selectedNode);
        } else {
            setSelectedContentDetails();
        }
    }, [selectedNode, selectedNodeIds]);

    const handleDrop = (newTreeData, options) => {

        const {dragSource, dropTarget} = options;
        const isServiceTypeDifferent = dragSource.serviceTypeId !== dropTarget.serviceTypeId;

        if (isServiceTypeDifferent) {
            setAlertMessage({children: "Moving content under a different service type is not allowed.", severity: "error"});
            return;
        }

        const {originalId: sourceNodeId, text: sourceNodeName} = dragSource;
        const {originalId: targetNodeId, text: targetNodeName} = dropTarget;
        let directoryStructure = sourceNodeName;
        let node = dragSource;

        while (node.type !== NODE_LEVEL.ROOT) {
            node = getDragTarget(node.parent);
            directoryStructure = `${node.text}/${directoryStructure}`;
        }

        if (dragSource.type === NODE_LEVEL.CHILD) {

            if (dropTarget.type === NODE_LEVEL.BRANCH) {
                navigate('/content-hierarchy/content-info/move', {state: {sourceNodeId, sourceNodeName, targetNodeId, targetNodeName}});
            } else if (dropTarget.type === NODE_LEVEL.ROOT) {
                navigate('/content-hierarchy/content-info/remove-parent', {state: {sourceNodeId, sourceNodeName, targetNodeName}});
            }

        } else if (dragSource.type === NODE_LEVEL.BRANCH) {

            if (dropTarget.type === NODE_LEVEL.BRANCH) {
                navigate('/content-hierarchy/content-folder/move', {state: {sourceNodeId, sourceNodeName, targetNodeId, targetNodeName}});
            } else if (dropTarget.type === "root") {
                navigate('/content-hierarchy/content-folder/remove-parent', {state: {sourceNodeId, sourceNodeName, targetNodeName}});
            }
        }
    }

    const handleCrudAction = (crudAction) => {
        navigate('/content-hierarchy/content-folder/update-content', {state: {data: selectedNode, crudAction}});
    }

    const handleSelect = (node) => {

        setEnableCreateFolder(node.type === NODE_LEVEL.ROOT || node.type === NODE_LEVEL.BRANCH);
        setEnableRenameFolder(node.type === NODE_LEVEL.BRANCH);
        setEnableDeleteFolder(node.type === NODE_LEVEL.BRANCH);

        setEnableAllContentActions(node.type === NODE_LEVEL.CHILD);
        setEnableNewContentAction(node.type !== NODE_LEVEL.CHILD);

        if (node.type === NODE_LEVEL.ROOT || node.type === NODE_LEVEL.BRANCH) {
            setSelectedNode(node);
        }

        setSelectedNodeIds([parseInt(node.id)]);
        setSelectedNode(node);
    }

    const updateNode = (node, depth, hasChild) => {
        let doesDropNodeExist = false;

        nodeList.map((value) => {
            if (value.id === node.id) {
                value.depth = depth;
                value.hasChild = hasChild;
                value.parent = node.parent;
                doesDropNodeExist = true;
            }
        });

        if (doesDropNodeExist) {
            setNodeList(nodeList);
        } else {
            node.depth = depth;
            node.hasChild = hasChild;
            nodeList.push(node);
            setNodeList(nodeList);
        }
    }

    const getDropTarget = (dropTargetId) => {
        let dropTarget;
        nodeList.map((value) => {
            if (value.id === dropTargetId) {
                dropTarget = value;
            }
        });

        return dropTarget;
    }

    const getDragTarget = (dragTargetId) => {
        let dragTarget;
        nodeList.map((value) => {
            if (value.id === dragTargetId) {
                dragTarget = value;
            }
        });

        return dragTarget;
    }

    const handleCloseCustomAlert = () => setAlertMessage(null);

    const theme = createTheme(customBackOfficeTheme, {
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    '*': {margin: 0, padding: 0},
                    'html, body, #root': {height: '100%'},
                    ul: {listStyle: 'none'}
                }
            },
            MuiSvgIcon: {styleOverrides: {root: {verticalAlign: 'middle'}}}
        }
    });

    return (
      <ThemeProvider theme={theme}>
          <CssBaseline/>
          <DndProvider backend={MultiBackend} options={getBackendOptions()}>
              <Grid container mb={0.5} pl={2}>
                <Button size="small" sx={{mt: 1, ml: 1}} variant="contained" disabled={!enableCreateFolder} onClick={() => handleCrudAction(CONTENT_ACTION_CREATE)} startIcon={<CreateNewFolderIcon />}>New</Button>
                <Button size="small" sx={{mt: 1, ml: 1}} variant="contained" disabled={!enableRenameFolder} onClick={() => handleCrudAction(CONTENT_ACTION_RENAME)} startIcon={<SourceIcon />}>Rename</Button>
                <Button size="small" sx={{mt: 1, ml: 1}} variant="contained" disabled={!enableDeleteFolder} onClick={() => handleCrudAction(CONTENT_ACTION_DELETE)} startIcon={<FolderDeleteIcon />}>Delete</Button>
              </Grid>
              <Grid container style={{height: 'calc(100vh - 8.5rem)'}} ref={treeRef}>
                  <Tree
                    tree={treeData}
                    rootId={0}
                    initialOpen={false}
                    render={(node, {depth, isOpen, onToggle, isDragging, isDropTarget, hasChild}) => (
                      <HierarchyNode
                        node={node}
                        depth={depth}
                        isOpen={isOpen}
                        isSelected={selectedNodeIds.includes(node.id) && !!selectedNode}
                        onToggle={onToggle}
                        onSelect={handleSelect}
                        isDragging={isDragging}
                        isDropTarget={isDropTarget}
                        hasChild={hasChild}
                        updateNode={value => updateNode(value, depth, hasChild)}
                        setSelectedContentDetails={id => setSelectedContentDetails(id)}
                        collapsedNodes={collapsedNodes}
                        setCollapsedNodes={setCollapsedNodes} />
                    )}
                    dragPreviewRender={monitorProps => <HierarchyNodeDragPreview monitorProps={monitorProps} />}
                    onDrop={handleDrop}
                    canDrag={dragSourceId => dragSourceId.parent !== 0}
                    canDrop={(treeData, {dragSource, dropTarget, dropTargetId, dragSourceId}) => {

                        if (dragSource === dropTarget) {
                            return false;
                        }

                        const dropTargetNode = getDropTarget(dropTargetId);
                        const dragTargetNode = getDragTarget(dragSourceId);

                        if (dropTargetNode === null || dropTargetNode === undefined || !dropTargetNode.droppable) {
                            return false;
                        }

                        const isDragSourceParentRoot = dragTargetNode.parent === 0;
                        const isDropTargetChildOfDragSource = dragTargetNode.id === dropTargetNode.parent;
                        const isDropOnItself = dropTargetNode.id === dragTargetNode.id;
                        const isInvalidDrop = !(isDragSourceParentRoot || isDropTargetChildOfDragSource || isDropOnItself);

                        return isInvalidDrop;
                    }}
                    classes={{root: treeRootStyle, draggingSource: draggingSourceStyle, dropTarget: dropTargetStyle}} />
              </Grid>
          </DndProvider>
          <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
      </ThemeProvider>
    );
}
